import * as React from 'react';
import styled, { css, ThemeContext } from "styled-components";
import Color from "color";
import { ListEntryAction } from './ListEntryAction';
import { useSwipeGesture } from '--floponents-web/hooks/useSwipeGesture';
import IconCheck from "@floji-internal/mui-icons/Check";

type ListSpacing = [number, number, number, number];
type ListIndentStyleNames = "sidebar" | "dropdownMenu" | "dropdownMenuDense" | "default" | "dense" | "call";
type ListIndentStyle = keyof typeof ListIndentStlyes | ListSpacing;

// I hate this and they should be moved out but because of the way this was originally written
// it is the best fastest solution for now
const ListIndentStlyes: { [key in ListIndentStyleNames]: ListSpacing } = {
    sidebar: [1, 2, 1, 2],                  // Main sidebar lists a little extra space left and right
    dropdownMenu: [1, 2, 1, 2],             // Dropdown menu, extra left right spacing
    dropdownMenuDense: [0.5, 2, 0.5, 2],    // Mobile alternative for the dropdown styling
    default: [1, 1, 1, 1],
    dense: [0.5, 1, 0.5, 1],                // Mobile alternative for the default spacing
    call: [1.5, 2, 1.5, 2]
}

type StyledListEntryProps = {
    clickable?: boolean
    active?: boolean
    border?: boolean
    rounded?: boolean
    width?: number | string
    padding?: string
    actionsWidth?: number;
    hideSecondaryActions?: boolean
}

type ListEntryProps = {
    active?: boolean
    indentStyle?: ListIndentStyle
    border?: boolean
    width?: number | string,
    disabled?: boolean
    hideSecondaryActions?: boolean;
    checked?: boolean;
    className?: string
}

const borderStyles = (rounded?: boolean, active?: boolean) => css`
    background-color: ${props => props.theme.palette.surface};
    border: 1px solid ${props => active ? props.theme.palette.primary.main : props.theme.palette.ui.border};
    border-radius: ${props => props.theme.radius.small};
    &:not(:last-child) {
        margin-bottom: ${props => props.theme.spacing(1)};
    }
`;

export const StyledListEntry = styled.li<StyledListEntryProps>`
    --drag-distance: 0; // Updated in JS

    position: relative;
    list-style: none;
    display: flex;
    flex: 0 0 auto;
    width: ${props => typeof props.width == "string" ? props.width : props.width ? props.width + "px" : "initial"};

    /* Border styles */
    ${props => props.border && borderStyles(props.rounded, props.active)}

    background: ${props => props.active
        ? Color(props.theme.palette.primary.main).alpha(0.2).toString()
        : "initial"
    };

    // Bog standard hover styles, only used when item is actually clickable
    :hover {
        background: ${props => props.clickable
        ? props.active
            ? Color(props.theme.palette.primary.main).alpha(0.35).toString()
            : props.theme.palette.ui.highlight
        : "initial"
    };
    }

    // Part of a ListSurface, a separate parent component controls highlighting with an attribute
    [data-ls-highlight='true'] && {
        background: ${props => props.active
        ? Color(props.theme.palette.primary.main).alpha(0.35).toString()
        : props.theme.palette.ui.highlight
    };
    }

    /* Main ListItem body */
    > div:first-of-type {
        display: flex;
        flex: 1 1 auto;
        overflow: hidden;
        align-items: center;
        user-select: none;
        padding: ${props => props.padding};
        cursor: ${props => props.clickable ? "pointer" : "inherit"};

        clip-path: inset(0 0 0 0);
        transform: translateX( calc( var(--drag-distance) * -1px ));

        transition: 
            background-color ${props => props.theme.transitions.toggle},
            transform ${props => props.theme.transitions.standard} ease-out;
    }
    
    /* Styles for "hideSecondaryActions" */
    ${props => props.hideSecondaryActions && css`
        /* Clip list item behind actions when hovering */
        :not(._dragging):hover > div:first-of-type {
            clip-path: inset(0 ${props.actionsWidth || 0}px 0 0);
        }

        /* Disable transform transition when dragging */
        &._dragging > div:first-of-type {
            transition: 
                background-color ${props => props.theme.transitions.toggle};
        }

        /* Set default opacity for secondary actions */
        ${ListEntryAction} {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            opacity: calc( var(--drag-distance) / ${props.actionsWidth || 0} );
            transition: opacity ${props.theme.transitions.toggle}; 
        }

        @media (hover: hover) {
            /* Hover opacity for list items */
            :not(._dragging):hover ${ListEntryAction} {
                opacity: 1;
            }
        }
    `}
`;

interface ListEntryCheckboxProps {
    checked: boolean;
}

const StyledListEntryCheckbox = styled.div.attrs<ListEntryCheckboxProps>(props => ({
    role: "checkbox",
    "aria-checked": props.checked
})) <ListEntryCheckboxProps>`
    color: ${props => props.theme.palette.text.primary};
    flex-shrink: 0;
    margin-right: ${props => props.theme.spacing(1)};
    width: 20px;
`;

const ListEntryCheckbox = (props: ListEntryCheckboxProps) => {
    return (
        <StyledListEntryCheckbox {...props}>
            {props.checked && <IconCheck size="20px" />}
        </StyledListEntryCheckbox>
    )
}

const getPadding = (indentStyle?: ListIndentStyle) => {
    if (Array.isArray(indentStyle)) return indentStyle;
    return ListIndentStlyes[indentStyle || "default"];
}

const ListEntry = React.forwardRef((props: ListEntryProps & React.HtmlHTMLAttributes<HTMLElement>, ref: React.RefObject<HTMLElement>) => {
    const { children: childrenProp, onClick, indentStyle, disabled, ...sleProps } = props;

    const contentRef = React.useRef<HTMLDivElement>(null);
    const actionsRef = React.useRef<HTMLDivElement>(null);
    const [actionsWidth, setActionsWidth] = React.useState(actionsRef.current?.offsetWidth || 0);

    const theme = React.useContext(ThemeContext);
    const padding = getPadding(props.indentStyle);
    const paddingPx = theme.spacing(...padding);

    // Work out if we have a secondary action...
    const children = React.Children.toArray(childrenProp);
    const lastChild = children.pop();
    const haveSecondaryAction = React.isValidElement(lastChild) && lastChild.type == ListEntryAction;

    const secondaryAction = haveSecondaryAction
        ? React.cloneElement(lastChild as React.ReactElement, { ref: actionsRef })
        : null;

    React.useLayoutEffect(() => {
        if (actionsRef.current)
            setActionsWidth(actionsRef.current.offsetWidth);
    }, []);

    // scroll active element into view 
    React.useEffect(() => {
        if (contentRef.current && props.active)
            contentRef.current.scrollIntoView({ behavior: "auto", block: "center" })
    }, []);

    const onSwipeActionsReset = React.useCallback(() => {
        if (!actionsRef.current) return;
        const listElement = actionsRef.current.parentElement!;
        listElement.style.setProperty("--drag-distance", "0");
        listElement.classList.remove("_dragging");
    }, [actionsRef]);

    const onSwipeActions = React.useCallback((distance: number, _, ended: boolean) => {
        if (!actionsRef.current) return;
        const listElement = actionsRef.current.parentElement!;
        const actionsWidth = actionsRef.current.offsetWidth;
        listElement.classList.add("_dragging");
        if (ended && actionsWidth) {
            listElement.classList.remove("_dragging");
            if (Math.floor(distance) > actionsWidth) {
                // Swipe has ended and user has swiped far enough to leave the menu open
                listElement.style.setProperty("--drag-distance", actionsWidth.toString(10));
            } else {
                // Swipe has ended close revert to the default state
                onSwipeActionsReset();
            }
        } else {
            // Swipe is in progress
            listElement.style.setProperty("--drag-distance", distance.toString(10));
        }
    }, [actionsRef]);

    useSwipeGesture({
        up: onSwipeActionsReset,
        down: onSwipeActionsReset,
        right: onSwipeActionsReset,
        disable: !props.hideSecondaryActions,
        target: actionsRef
    });

    useSwipeGesture({
        target: contentRef,
        left: onSwipeActions,
        disable: !props.hideSecondaryActions
    });

    return <StyledListEntry
        {...sleProps as any}
        indentStyle={indentStyle}
        actionsWidth={actionsWidth}
        hideSecondaryActions={props.hideSecondaryActions}
        clickable={!disabled && !!onClick}
        padding={paddingPx}
        ref={ref as any}>
        <div onClick={onClick} ref={contentRef}>
            {props.checked !== undefined && (
                <ListEntryCheckbox checked={props.checked} />
            )}
            {children}
            {!secondaryAction && lastChild}
        </div>
        {secondaryAction}
    </StyledListEntry>
});
ListEntry.displayName = ListEntry.name;


export default ListEntry;
export { ListEntry, ListEntryProps, ListIndentStyle };